import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';
import TitleBox from '../../TitleBox';
import {findDiscountBand} from './discountTable';
import {
  type CalculationResult,
  type DealFormValues,
  calculateDiscount,
  formatCurrency,
  formatPercent,
} from './utils';

const INITIAL_FORM: DealFormValues = {
  tcv: '',
  years: '',
  dealType: '',
  paymentFreq: '',
  tier: '',
  monthlyMin: '',
  products: {serverless: false, assistant: false, byoc: false},
};

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
    gap: 3,
  },
  formPaper: {
    p: 3,
  },
  resultsPaper: {
    p: 3,
    backgroundColor: '#0a1f1a',
    color: '#fff',
  },
  sectionTitle: {
    fontWeight: 600,
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '&::before': {
      content: '""',
      width: 4,
      height: 24,
      backgroundColor: 'primary.main',
      borderRadius: 1,
      display: 'inline-block',
    },
  },
  resultItem: {
    p: 2,
    mb: 1.5,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
    border: '1px solid rgba(0,212,170,0.2)',
  },
  resultLabel: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.7)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: 'monospace',
    mb: 0.5,
  },
  resultValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#00ffcc',
    fontFamily: 'monospace',
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    mb: 0.5,
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.8)',
  },
  breakdownDivider: {
    borderTop: '1px solid rgba(0,212,170,0.3)',
    pt: 0.5,
    mt: 0.5,
    fontWeight: 600,
  },
} as const;

const PAYMENT_FREQ_OPTIONS = [
  {value: 'quarterly', label: 'Quarterly', years: null},
  {value: 'semi', label: 'Semi-annually', years: null},
  {value: 'annual', label: 'Annual', years: null},
  {value: 'upfront2', label: 'Upfront (2 years)', years: 2},
  {value: 'upfront3', label: 'Upfront (3 years)', years: 3},
];

function ResultsPanel({result}: {result: CalculationResult | null}) {
  if (!result) {
    return (
      <Alert severity="info" variant="outlined">
        <Typography fontWeight={600} gutterBottom>
          Instructions
        </Typography>
        <Box component="ul" sx={{pl: 2, m: 0}}>
          <li>Enter all deal parameters on the left</li>
          <li>Select at least one product</li>
          <li>Click &quot;Calculate discount&quot; to see results</li>
          <li>Results show maximum allowable discounts</li>
        </Box>
      </Alert>
    );
  }

  if (result.type === 'error') {
    return <Alert severity="warning">{result.message}</Alert>;
  }

  const {tcv, acv, band, productDiscounts, warnings} = result;

  return (
    <>
      <Box sx={styles.resultItem}>
        <Typography sx={styles.resultLabel}>Total contract value (TCV)</Typography>
        <Typography sx={styles.resultValue}>{formatCurrency(tcv)}</Typography>
      </Box>

      <Box sx={styles.resultItem}>
        <Typography sx={styles.resultLabel}>Annual contract value (ACV)</Typography>
        <Typography sx={styles.resultValue}>{formatCurrency(acv)}</Typography>
      </Box>

      <Box sx={styles.resultItem}>
        <Typography sx={styles.resultLabel}>Discount band</Typography>
        <Typography sx={styles.resultValue}>
          {formatCurrency(band.min)} &ndash; {formatCurrency(band.max)}
        </Typography>
      </Box>

      {productDiscounts.map((product) => (
        <Box key={product.name} sx={styles.resultItem}>
          <Typography sx={styles.resultLabel}>{product.name}</Typography>
          <Typography sx={styles.resultValue}>{formatPercent(product.discount)}</Typography>

          <Box sx={{mt: 1, pt: 1, borderTop: '1px solid rgba(0,212,170,0.2)'}}>
            <Box sx={styles.breakdownRow}>
              <span>Base discount (rep):</span>
              <span>{formatPercent(product.baseComponents.base)}</span>
            </Box>

            {product.baseComponents.paymentTerm !== 0 && (
              <Box sx={styles.breakdownRow}>
                <span>Payment terms:</span>
                <span>{formatPercent(product.baseComponents.paymentTerm)}</span>
              </Box>
            )}

            {product.baseComponents.newDeal !== 0 && (
              <Box sx={styles.breakdownRow}>
                <span>New deal:</span>
                <span>+{formatPercent(product.baseComponents.newDeal)}</span>
              </Box>
            )}

            {product.baseComponents.enterprise !== 0 && (
              <Box sx={styles.breakdownRow}>
                <span>Enterprise tier:</span>
                <span>+{formatPercent(product.baseComponents.enterprise)}</span>
              </Box>
            )}

            {product.baseComponents.monthlyMin !== 0 && (
              <Box sx={styles.breakdownRow}>
                <span>Monthly min (&ge;80%):</span>
                <span>+{formatPercent(product.baseComponents.monthlyMin)}</span>
              </Box>
            )}

            {product.addon !== undefined && (
              <Box sx={styles.breakdownRow}>
                <span>{product.name} add-on:</span>
                <span>+{formatPercent(product.addon)}</span>
              </Box>
            )}

            {product.isCapped && (
              <Box sx={{...styles.breakdownRow, ...styles.breakdownDivider}}>
                <span>Maximum allowed:</span>
                <span>{formatPercent(product.max)}</span>
              </Box>
            )}
          </Box>
        </Box>
      ))}

      {warnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{mt: 1}}>
          {warning}
        </Alert>
      ))}
    </>
  );
}

export default function DiscountCalculatorPage() {
  const [form, setForm] = useState<DealFormValues>(INITIAL_FORM);
  const [result, setResult] = useState<CalculationResult | null>(null);

  const updateField = useCallback(
    <K extends keyof DealFormValues>(field: K, value: DealFormValues[K]) => {
      setForm((prev) => ({...prev, [field]: value}));
    },
    [],
  );

  const toggleProduct = useCallback((product: keyof DealFormValues['products']) => {
    setForm((prev) => ({
      ...prev,
      products: {...prev.products, [product]: !prev.products[product]},
    }));
  }, []);

  const paymentFreqOptions = useMemo(() => {
    const years = parseInt(form.years, 10);
    return PAYMENT_FREQ_OPTIONS.filter((opt) => opt.years === null || opt.years === years);
  }, [form.years]);

  const handleYearsChange = useCallback(
    (years: string) => {
      const yearsNum = parseInt(years, 10);
      updateField('years', years);
      if (
        (form.paymentFreq === 'upfront2' && yearsNum !== 2) ||
        (form.paymentFreq === 'upfront3' && yearsNum !== 3)
      ) {
        updateField('paymentFreq', '');
      }
    },
    [form.paymentFreq, updateField],
  );

  const handleCalculate = useCallback(() => {
    setResult(calculateDiscount(form, findDiscountBand));
  }, [form]);

  return (
    <Box mb={8}>
      <TitleBox pageTitle="Discount calculator" />

      <Box sx={styles.grid}>
        <Paper sx={styles.formPaper}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            Deal parameters
          </Typography>

          <TextField
            label="Total contract value (TCV)"
            type="number"
            fullWidth
            margin="normal"
            placeholder="e.g. 50000"
            value={form.tcv}
            onChange={(e) => updateField('tcv', e.target.value)}
            slotProps={{htmlInput: {min: 0, step: 1000}}}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Contract length</InputLabel>
            <Select
              label="Contract length"
              value={form.years}
              onChange={(e) => handleYearsChange(e.target.value)}
            >
              <MenuItem value="1">1 year</MenuItem>
              <MenuItem value="2">2 years</MenuItem>
              <MenuItem value="3">3 years</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Deal type</InputLabel>
            <Select
              label="Deal type"
              value={form.dealType}
              onChange={(e) => updateField('dealType', e.target.value)}
            >
              <MenuItem value="new">New deal</MenuItem>
              <MenuItem value="renewal">Renewal / expansion</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Payment frequency</InputLabel>
            <Select
              label="Payment frequency"
              value={form.paymentFreq}
              onChange={(e) => updateField('paymentFreq', e.target.value)}
            >
              {paymentFreqOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Tier</InputLabel>
            <Select
              label="Tier"
              value={form.tier}
              onChange={(e) => updateField('tier', e.target.value)}
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Monthly minimum (% of commitment)"
            type="number"
            fullWidth
            margin="normal"
            placeholder="e.g. 80"
            value={form.monthlyMin}
            onChange={(e) => updateField('monthlyMin', e.target.value)}
            slotProps={{htmlInput: {min: 0, max: 100, step: 1}}}
          />

          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Products</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.products.serverless}
                    onChange={() => toggleProduct('serverless')}
                  />
                }
                label="Serverless (Database)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.products.assistant}
                    onChange={() => toggleProduct('assistant')}
                  />
                }
                label="Assistant"
              />
              <FormControlLabel
                control={
                  <Checkbox checked={form.products.byoc} onChange={() => toggleProduct('byoc')} />
                }
                label="BYOC (Bring Your Own Cloud)"
              />
            </FormGroup>
          </FormControl>

          <Button variant="contained" fullWidth size="large" sx={{mt: 2}} onClick={handleCalculate}>
            Calculate discount
          </Button>
        </Paper>

        <Paper sx={styles.resultsPaper}>
          <Typography variant="h6" sx={{...styles.sectionTitle, color: '#00ffcc'}}>
            Discount results
          </Typography>
          <ResultsPanel result={result} />
        </Paper>
      </Box>
    </Box>
  );
}
